import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { useCallback, useMemo } from 'react';
import { CustomSelectProps, CustomSelectBase, CustomSelectHeaderDefaultProps } from '..';
import TwinIcon from '../../../baseComponents/TwinIcon';
import { TwinDictionary } from '../../../utils/globals/dictionary';
import { CustomSelectDropdownIcon, CustomSelectDropDown, useDropDownRowLogic, useDropDownWithSearchBarLogic } from '../DropDown';
import { CustomSelectDropDownBaseProps, CustomSelectDropDownRowProps } from '../DropDown/types';
import { DisplayTextFuncTy } from '../types';
import TwinToolTip from '../../../baseComponents/TwinToolTip';
import useTwinTranslation from '../../../utils/hooks/useTwinTranslation';
import SearchBar from '../../../baseComponents/SearchBar';
import { dictIcon } from '../../../utils/icons';

interface CustomSelectIconProps extends CustomSelectProps {}

export const CustomSelectIcon: React.FC<CustomSelectIconProps> = ({ value, label, onChange, items, extraDropDownParams, className, ...rest }) => {
    const { displayTextFunc } = useCustomSelectIconLogic({fieldName: rest.fieldName})
    const itemsDict = JSON.parse(JSON.stringify(items))
    return (
        <div className='w-full' onClick={(e) => { e.preventDefault(); e.stopPropagation() }}>
            <CustomSelectBase {...rest} className={'custom_select_label_up w-full px-4 ' + (className || '')} CustomSelectHeader={CustomSelectIconHeader} onChange={onChange} label={label || ''} items={itemsDict} value={value} displayTextFunc={displayTextFunc} DropDownComponent={CustomSelectIconDropDownWithSearchBar} extraDropDownParams={extraDropDownParams} />
        </div>
    )
}

interface CustomSelectIconLogicProps {
    fieldName?: string
}
const useCustomSelectIconLogic = ({ fieldName = 'name' }: CustomSelectIconLogicProps) => {
    const displayTextFunc: DisplayTextFuncTy = useCallback(((items, value) => {
        if (!Array.isArray(items)) {
            return items[value]?.[fieldName]
        }
        return ''
    }), [fieldName])

    return { displayTextFunc }
}


interface CustomSelectIconHeaderProps extends CustomSelectHeaderDefaultProps { }

const CustomSelectIconHeader: React.FC<CustomSelectIconHeaderProps> = ({ opened, displayText, label, required, icon, extraHeaderParams, items, placeholder, ...input }) => {
    const myValueItem = items[(input.value || '') as any]
    return (
        <div className={'custom_select_header custom_select_header_white_input items-center cursor-pointer '}>
            {extraHeaderParams?.displayToolTip ? <TwinToolTip id={extraHeaderParams.idTooltip} place='top' content={extraHeaderParams.message} />: null}
            <input type='text' className='input_hidden' {...input} required={required} onChange={() => undefined} />
            <div className='custom_select_header_icon' data-tooltip-id={extraHeaderParams?.idTooltip}>
                <div className='custom_select_header_text'>
                    <div className='flex items-center custom_select_header_container'>
                        {myValueItem?.icon ? <TwinIcon className='mr-10 custom_select_icon_icon' icon={dictIcon[myValueItem.icon]} /> : null}
                        {displayText !== '-' ?
                            <div className='custom_select_item_text' title={displayText}>
                                {displayText !== 'main' ? displayText : <TwinIcon icon={faChevronDown} />}
                            </div>
                            :
                            <TwinIcon className='custom_select_block_icon absolute text-white h-10 w-10' icon={faChevronDown} />
                        }
                    </div>
                </div>
                <CustomSelectDropdownIcon opened={opened} readOnly={input.readOnly} icon={icon} />
            </div>
        </div>
    )
}

interface CustomSelectIconDropDownProps extends CustomSelectDropDownBaseProps {
    fieldDescription?: string
}

export const CustomSelectIconDropDown: React.FC<CustomSelectIconDropDownProps> = ({ items, children, fieldName = 'name', fieldDescription = 'name', ...rest }) => {
    const itemsSorted = useMemo(() => sortCustomSelectIcons(items, fieldName, fieldDescription), [items, fieldDescription, fieldName])
    return (<CustomSelectDropDown items={itemsSorted} {...rest} rowRenderer={({ ...rowRenderProps }) => {
        return (
            <CustomSelectIconDropDownRow currentvalue={rest.value} onChange={rest.onChange} item={itemsSorted[rowRenderProps.index]}  {...rowRenderProps} />
        )
    }}>{children}</CustomSelectDropDown>)
}


export const CustomSelectIconDropDownWithSearchBar: React.FC<CustomSelectIconDropDownProps> = ({ ...props }) => {
    const { t } = useTwinTranslation()
    const { searchValue, changeSearchValue, filteredItems } = useDropDownWithSearchBarLogic(props.items, props.fieldName)
    return (
        <CustomSelectIconDropDown {...props} items={filteredItems}>
            <SearchBar className='custom_select_search_bar' placeholder={t('search', 'Buscar')} onChange={changeSearchValue} {...{ searchValue }} />
        </CustomSelectIconDropDown>
    )
}


interface CustomSelectIconDropDownRowTy {
    text: string | number
    value: string | number
    description: string
    icon: string
    order: string | number
}

export const sortCustomSelectIcons = (items: TwinDictionary, fieldName = 'name', fieldDescription = 'name') => {
    const itemsArr: CustomSelectIconDropDownRowTy[] = []
    for (const key in items) {
        const item = items[key]
        itemsArr.push({ value: key, text: item[fieldName], order: item.order || key, icon: item.icon, description: (item[fieldDescription] || item[fieldName]) })
    }
    itemsArr.sort((a, b) => {
        return  a.order > b.order ? 1 : -1
    })
    return itemsArr
}

const CustomSelectIconDropDownRow: React.FC<CustomSelectDropDownRowProps> = ({ className, onChange, ...rest }) => {
    const { icon, description } = rest.item
    const { changeValue, text, value } = useDropDownRowLogic({ onChange, ...rest })
    return (
        <div className={'custom_select_value cursor-pointer flex justify-between items-center bg-white regular12 text-gray-51 hover:bg-gray-F7 px-15 ' + (className || '')} title={description} onClick={changeValue} {...rest} {...value}>
            {icon ? <TwinIcon className='mr-10 custom_select_icon_icon min-w-16 w-16' icon={dictIcon[icon]} /> : null}
            <span className='w-full custom_select_item_row_text'>{text}</span>
        </div>
    )
}

export default CustomSelectIcon